import { GameState } from '@susdle/shared';

export interface ColyseusRoomPort<S = GameState> {
  readonly reconnectionToken: string;
  readonly state: S;
  onStateChange(cb: (state: S) => void): void;
  /**
   * onLeave callback receives (code, reason). `reason` is the canonical
   * schema-mismatch signal — close codes vary across Colyseus versions, but
   * the room can pass a stable `reason: 'schemaMismatch'` string. Epic 15
   * plumbs this end-to-end.
   */
  onLeave(cb: (code: number, reason?: string) => void): void;
  send<T extends string>(type: T, message?: unknown): void;
  /** Subscribe to a typed server→client message. Mirrors colyseus.js Room.onMessage. */
  onMessage<T = unknown>(type: string, cb: (message: T) => void): void;
  /** Real Room.leave returns Promise<number> (close code). Port mirrors this. */
  leave(): Promise<number>;
}

export interface ColyseusClientPort {
  joinOrCreate(roomName: string, options?: unknown): Promise<ColyseusRoomPort>;
  reconnect(reconnectionToken: string): Promise<ColyseusRoomPort>;
}

import { Client as RealClient } from 'colyseus.js';

export class RealColyseusClient implements ColyseusClientPort {
  private readonly client: RealClient;
  constructor(endpoint: string) { this.client = new RealClient(endpoint); }
  async joinOrCreate(roomName: string, options?: unknown): Promise<ColyseusRoomPort> {
    const room = await this.client.joinOrCreate<GameState>(roomName, (options as Record<string, unknown>) ?? {}, GameState);
    return wrap(room);
  }
  async reconnect(reconnectionToken: string): Promise<ColyseusRoomPort> {
    const room = await this.client.reconnect<GameState>(reconnectionToken, GameState);
    return wrap(room);
  }
}

function wrap(room: import('colyseus.js').Room<GameState>): ColyseusRoomPort {
  return {
    get reconnectionToken() { return room.reconnectionToken; },
    get state() { return room.state; },
    onStateChange(cb) { room.onStateChange(cb); },
    onLeave(cb) { room.onLeave(cb); },
    send(type, message) { room.send(type, message); },
    onMessage(type, cb) { room.onMessage(type, cb); },
    leave() { return room.leave(); },
  };
}
